'use client';

import React, { PropsWithChildren, useEffect, useRef } from 'react';
import { DrawerHeader, NavigationDrawer } from './NavigationDrawer';
import { Box, BoxProps, GlobalStyles, useMediaQuery } from '@mui/material';
import { useAppDispatch, useAppSelector, useDesktopQuery } from '@/hooks';
import { useToggle } from '@ianesthesie-kit/core';
import { PaddedWrapper } from '@/components/paddedWrapper';
import { motion } from 'framer-motion';
import { AppBar } from './AppBar';
import { FloatingActions, FloatingActionsProvider } from './floatingActions';
import isEmpty from 'lodash/fp/isEmpty';
import { type RootState } from '@/store';
import { styled, Theme } from '@mui/material/styles';
import { AppConfigService, appBarHeight } from '@/config';
import { closeAllDrawers } from '@/modules/appContext';
import Drawer from '@mui/material/Drawer';
import { createSelector } from 'reselect';
import { useDelayedValue } from '@ianesthesie-kit/delay';
import {
  MedicationDrawerContent,
  PremedicationDrawerContent,
  NewPatientDrawerContent,
  ProtocolDrawerContent,
  ScoresDrawerContent,
  TemplatesDrawerContent,
  EpicDocumentsDrawerContent,
  ScSuggestionDrawerContent,
  MedicationInstructionsDrawerContent,
  PatientsAppDetailsDrawerContent,
  CustomDocumentDrawerContent,
  BillingPatientDrawerContent
} from '@/components/drawer';
import { SplashScreenLoader } from '@/components/loader';
import { DeskPaymentRegistryRefreshers } from '@/components/billing/deskPayment/DeskPaymentRegistryRefreshers';

const navDrawerWidth = 300;

type FilterEndingWith<
  Set,
  Needle extends string
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
> = Set extends `${infer _X}${Needle}` ? Set : never;

type DrawerStateName = FilterEndingWith<
  keyof RootState['appContext'],
  'Drawer'
>;

const selectContextState = (state: RootState) => state.appContext;

const selectOpenDrawer = createSelector(selectContextState, (contextState) => {
  if (!contextState) {
    return undefined;
  }
  return Object.entries(contextState)
    .filter(([k]) => k.endsWith('Drawer'))
    .find(([, drawerState]) => drawerState.open)?.[0] as
    | DrawerStateName
    | undefined;
});

const DrawersContainer = ({ children }: PropsWithChildren<unknown>) => {
  const dispatch = useAppDispatch();
  const openDrawer = useAppSelector(selectOpenDrawer);
  const [delayedOpenDrawer] = useDelayedValue(openDrawer, { delayMs: 250 });
  const smoothOpen = openDrawer ?? delayedOpenDrawer;
  return (
    <>
      {children}
      {/* Above the drawer below, which unmounts its content 250 ms after it closes: a
          refresher held there would go with it while the card still waits to be tapped. */}
      <DeskPaymentRegistryRefreshers />
      <Drawer
        open={!!openDrawer}
        anchor="right"
        variant="temporary"
        onClose={() => dispatch(closeAllDrawers())}
      >
        {smoothOpen === 'templateDrawer' && <TemplatesDrawerContent />}
        {smoothOpen === 'protocolDrawer' && <ProtocolDrawerContent />}
        {smoothOpen === 'newPatientDrawer' && <NewPatientDrawerContent />}
        {smoothOpen === 'medicalScoresDrawer' && <ScoresDrawerContent />}
        {smoothOpen === 'medicationDrawer' && <MedicationDrawerContent />}
        {smoothOpen === 'premedicationDrawer' && <PremedicationDrawerContent />}
        {smoothOpen === 'epicDocumentsDrawer' && <EpicDocumentsDrawerContent />}
        {smoothOpen === 'scSuggestionDrawer' && <ScSuggestionDrawerContent />}
        {smoothOpen === 'medicationInstructionsDrawer' && (
          <MedicationInstructionsDrawerContent />
        )}
        {smoothOpen === 'patientsAppDetailsDrawer' && (
          <PatientsAppDetailsDrawerContent />
        )}
        {smoothOpen === 'customDocumentDrawer' && (
          <CustomDocumentDrawerContent />
        )}
        {smoothOpen === 'billingPatientDrawer' && (
          <BillingPatientDrawerContent />
        )}
      </Drawer>
    </>
  );
};

const Main = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'drawerWidth'
})<BoxProps & { open: boolean; drawerWidth: number }>(
  ({ theme, open, drawerWidth }) => ({
    marginLeft: `-${drawerWidth}px`,
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    ...(open && {
      marginLeft: 0,
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen
      })
    })
  })
);

export const selectUserAuthenticated = (state: RootState) =>
  !state.auth.loading &&
  !isEmpty(state.auth.user) &&
  !state.auth.user.locked &&
  state.auth.user.enabled;

type AuthenticatedLayoutProps = PropsWithChildren<{
  disablePadding?: boolean;
}>;

export const AuthenticatedLayout: React.FC<AuthenticatedLayoutProps> = (
  props
) => {
  const isUserAuthenticated = useAppSelector(selectUserAuthenticated);
  const dummyAnchorRef = useRef<HTMLDivElement | null>(null);
  const onDesktop = useDesktopQuery();
  const isXlAndUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('xl'));
  const {
    toggledOn: drawerOpen,
    toggleOff: closeDrawer,
    toggleOn: openDrower,
    toggle: toggleDrawer
  } = useToggle(AppConfigService.getInstance().env !== 'dev'); // default open cause it screws up intercom Product Tour on small screens https://app.clickup.com/t/862jca6nm
  useEffect(() => {
    if (isXlAndUp) {
      openDrower();
    }
  }, [isXlAndUp, openDrower]);

  const Wrapper = props.disablePadding ? Box : PaddedWrapper;

  if (!isUserAuthenticated) {
    return <SplashScreenLoader />;
  }

  return (
    <DrawersContainer>
      <FloatingActionsProvider>
        {/* Cards scrolled into view stop below the fixed app bar instead of under it */}
        <GlobalStyles styles={{ html: { scrollPaddingTop: appBarHeight } }} />
        <Box display="flex" maxWidth="100vw" minWidth="100vw" overflow="auto">
          <motion.div initial={false} animate={drawerOpen ? 'open' : 'closed'}>
            <AppBar
              elevation={4}
              drawerWidth={navDrawerWidth}
              open={onDesktop && drawerOpen}
              onToggleClick={() => toggleDrawer()}
            />
            <NavigationDrawer
              onClose={closeDrawer}
              drawerWidth={navDrawerWidth}
              open={drawerOpen}
              variant={isXlAndUp ? 'persistent' : 'temporary'}
            />
          </motion.div>
          <Main
            component="main"
            flexGrow={1}
            style={{ overflowX: 'auto' }}
            drawerWidth={navDrawerWidth}
            open={!isXlAndUp || drawerOpen}
          >
            <div ref={dummyAnchorRef} />
            <DrawerHeader />
            <Wrapper mt={3}>{props.children}</Wrapper>
          </Main>
          <FloatingActions scrollTopAnchorRef={dummyAnchorRef} />
        </Box>
      </FloatingActionsProvider>
    </DrawersContainer>
  );
};
